import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  PlayCircle,
  PauseCircle,
  Edit3,
  ChevronDown,
  X,
  Upload,
  List,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { timesheetService } from '@/services/timesheet.service';
import { getEmployeesForDropdown, getEmployeesForMentions, MentionUser } from '@/services/employee.service';
import { TimesheetEntry } from '@/types/models';
import { useToast } from '@/components/ui/use-toast';
import { toast as sonnerToast } from 'sonner';
import { EditTaskDialog } from './EditTaskDialog';
import { useAuthStore } from '@/stores/authStore';
import taskService from '@/services/task.service';
import { baseURL } from '@/lib/axios';
import commentService, { Comment } from '@/services/comment.service';

type TaskStatusValue = string | { name?: string; status?: string } | null;

interface TaskDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: {
    id: string;
    name: string;
    description?: string;
    status: TaskStatusValue;
    priority: string;
    startDate: string;
    dueDate: string;
    assignedTo: {
      name: string;
      initials: string;
      avatar?: string | null;
    };
    assignees?: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      profilePicture?: string | null;
      name?: string;
    }>;
    followers?: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      profilePicture?: string | null;
      name?: string;
    }>;
    tags: string[];
    estimatedHours: number;
    url?: string;
    isPublic?: boolean;
    isBillable?: boolean;
    isVisibleToCustomer?: boolean;
  } | null;
  onTaskUpdate?: (taskId: string, updates: any) => void;
}

// Helper: get initials from a full name
const getInitialsFromName = (fullName?: string) => {
  if (!fullName) return 'UN';
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) || '';
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : (parts[0]?.charAt(1) || '');
  const initials = `${first}${last}`.toUpperCase();
  return initials || 'UN';
};

const getStatusIcon = (status: TaskStatusValue) => {
  // Extract status string if it's an object
  const statusStr = typeof status === 'object' && status !== null 
    ? (status.name || status.status || String(status)) 
    : String(status || 'Not Started');
  
  switch (statusStr) {
    case 'Not Started':
      return <AlertCircle className="h-4 w-4" />;
    case 'In Progress':
      return <PlayCircle className="h-4 w-4" />;
    case 'Testing':
      return <CheckCircle2 className="h-4 w-4" />;
    case 'Awaiting Feedback':
      return <Clock className="h-4 w-4" />;
    case 'Ready for Production':
      return <CheckCircle2 className="h-4 w-4" />;
    case 'On Hold':
      return <PauseCircle className="h-4 w-4" />;
    case 'Complete':
      return <CheckCircle2 className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

const getStatusColor = (status: TaskStatusValue) => {
  // Extract status string if it's an object
  const statusStr = typeof status === 'object' && status !== null 
    ? (status.name || status.status || String(status)) 
    : String(status || 'Not Started');
  
  switch (statusStr) {
    case 'Not Started':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'In Progress':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Testing':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Awaiting Feedback':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Ready for Production':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'On Hold':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Complete':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrlIfNeeded = (path?: string | null) => {
  if (!path) return null;
  if (path.startsWith('http')) {
    return path;
  }
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const buildFullName = (person: any) => `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

const matchPersonByNameOrEmail = (collection: any[] | undefined, value?: string) => {
  if (!collection || !value) return undefined;
  const normalized = value.trim().toLowerCase();
  return collection.find((person) => {
    if (!person) return false;
    const fullName = buildFullName(person).toLowerCase();
    if (fullName && fullName === normalized) return true;
    if (person.email && person.email.toLowerCase() === normalized) return true;
    if (person.name && person.name.toLowerCase() === normalized) return true;
    return false;
  });
};

export function TaskDetailDialog({ open, onOpenChange, task, onTaskUpdate }: TaskDetailDialogProps) {
  const { toast } = useToast();
  const { hasPermission, user } = useAuthStore();

  // Timer state
  const [timerState, setTimerState] = useState<'stopped' | 'running'>('stopped');
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [activeTimerEntry, setActiveTimerEntry] = useState<TimesheetEntry | null>(null);
  
  // Timesheet state
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([]);
  const [isLoadingTimesheet, setIsLoadingTimesheet] = useState(false);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerNotes, setTimerNotes] = useState('');
  
  // Editable fields state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [description, setDescription] = useState(task?.description || '');
  const [originalDescription, setOriginalDescription] = useState(task?.description || '');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(task ? [task.assignedTo.name] : []);
  const [selectedFollowers, setSelectedFollowers] = useState<string[]>(task ? [task.assignedTo.name] : []);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showFollowerDropdown, setShowFollowerDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [followerSearch, setFollowerSearch] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ id: string; _id?: string; name: string; email?: string }>>([]);
  // Internal loading flag for employee fetch
  const [, setIsLoadingEmployees] = useState<boolean>(false);
  // Loading states for assignees and followers updates
  const [isUpdatingAssignees, setIsUpdatingAssignees] = useState(false);
  const [isUpdatingFollowers, setIsUpdatingFollowers] = useState(false);
  // Inline edit drawer state
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editStartTime, setEditStartTime] = useState<string>('');
  const [editEndTime, setEditEndTime] = useState<string>('');
  const [editMember, setEditMember] = useState<string>('');
  const [editNote, setEditNote] = useState<string>('');
  
  // Edit task dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Local task loaded from API
  const [loadedTask, setLoadedTask] = useState<typeof task>(task);

  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [isDeleteCommentDialogOpen, setIsDeleteCommentDialogOpen] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  
  // Mention/tagging state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([]);
  const [allMentionUsers, setAllMentionUsers] = useState<MentionUser[]>([]); // All users for exact matching
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [taggedUsers, setTaggedUsers] = useState<Array<{ userId: string; name: string; position: number }>>([]);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  const mapApiTaskToUi = (api: any) => {
    const normalizeStatus = (s?: string | any) => {
      if (!s) return 'Not Started';
      
      // Extract status name if it's an object
      let statusValue = s;
      if (typeof s === 'object' && s !== null) {
        statusValue = s.name || s.status || s.title || '';
      }
      
      if (!statusValue) return 'Not Started';
      
      const statusStr = String(statusValue);
      const map: Record<string, string> = {
        NOT_STARTED: 'Not Started',
        IN_PROGRESS: 'In Progress',
        TESTING: 'Testing',
        AWAITING_FEEDBACK: 'Awaiting Feedback',
        READY_FOR_PRODUCTION: 'Ready for Production',
        ON_HOLD: 'On Hold',
        COMPLETE: 'Complete',
        COMPLETED: 'Complete',
      };
      return map[statusStr.toUpperCase()] || statusStr;
    };
    return {
      id: api._id,
      name: api.title,
      description: api.description,
      status: normalizeStatus(api.status),
      priority: api.priority || 'Medium',
      startDate: api.startDate,
      dueDate: api.dueDate,
      assignedTo: {
        name: api.assignees?.[0] ? `${api.assignees[0].firstName || ''} ${api.assignees[0].lastName || ''}`.trim() || api.assignees[0].email : 'Unassigned',
        initials: getInitialsFromName(
          api.assignees?.[0] ? `${api.assignees[0].firstName || ''} ${api.assignees[0].lastName || ''}`.trim() || api.assignees[0].email : 'UN'
        ),
        avatar: buildImageUrlIfNeeded(api.assignees?.[0]?.profilePicture),
      },
      assignees: (api.assignees || []).map((assignee: any) => ({
        ...assignee,
      })),
      followers: (api.followers || []).map((follower: any) => ({
        ...follower,
      })),
      tags: (api.tags || []).map((tag: any) => 
        typeof tag === 'string' ? tag : (tag?.name || tag?.title || String(tag))
      ),
      estimatedHours: api.estimateHours || 0,
      url: api.url || '',
    };
  };

  // Fetch latest task details when dialog opens
  useEffect(() => {
    const fetchTask = async () => {
      if (!open || !task?.id) return;
      try {
        const apiTask = await taskService.getTaskById(task.id);
        const uiTask = mapApiTaskToUi(apiTask as any);
        setLoadedTask(uiTask as any);
        // Sync editable local states
        const taskDescription = uiTask.description || '';
        setDescription(taskDescription);
        setOriginalDescription(taskDescription);
        if (uiTask.assignees && uiTask.assignees.length > 0) {
          setSelectedAssignees(uiTask.assignees.map((a: any) => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email));
        } else if (uiTask.assignedTo?.name) {
          setSelectedAssignees([uiTask.assignedTo.name]);
        }
        if (uiTask.followers && uiTask.followers.length > 0) {
          setSelectedFollowers(uiTask.followers.map((f: any) => `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.email));
        }
      } catch (error) {
        console.error('Failed to load task details:', error);
      }
    };
    fetchTask();
  }, [open, task?.id]);

  const currentTask = (loadedTask || task);

  // Update internal state when task prop changes
  useEffect(() => {
    if (task) {
      const taskDescription = task.description || '';
      setDescription(taskDescription);
      setOriginalDescription(taskDescription);
      setSelectedAssignees(task.assignedTo ? [task.assignedTo.name] : []);
      setSelectedFollowers(task.assignedTo ? [task.assignedTo.name] : []);
    }
  }, [task]);

  // Load timesheet entries when dialog opens
  const loadTimesheetEntries = useCallback(async () => {
    if (!currentTask?.id) return;
    setIsLoadingTimesheet(true);
    try {
      const entries = await timesheetService.getTimesheetEntriesByTask(currentTask.id);
      console.log('[TaskDetailDialog] Timesheet entries received:', entries);
      console.log('[TaskDetailDialog] Sample entry details:', entries[0]);
      setTimesheets(entries);
      
      // Check for active timer (entry without endTime) for the current user
      const activeEntry = entries.find((entry: TimesheetEntry) => 
        !entry.endTime && 
        (entry.employeeId === user?.id || entry.employee?._id === user?.id)
      );
      if (activeEntry) {
        console.log('[TaskDetailDialog] Found active timer:', activeEntry);
        setActiveTimerEntry(activeEntry);
        setTimerState('running');
        // Calculate elapsed seconds
        const startTime = new Date(activeEntry.startTime).getTime();
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        setTimerSeconds(elapsedSeconds);
      } else {
        setActiveTimerEntry(null);
        setTimerState('stopped');
        setTimerSeconds(0);
      }
    } catch (error: any) {
      console.error('Error loading timesheet entries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load timesheet entries',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTimesheet(false);
    }
  }, [currentTask?.id, toast, user?.id]);

  // Load comments for the task
  const loadComments = useCallback(async () => {
    if (!currentTask?.id) return;
    setIsLoadingComments(true);
    try {
      const fetchedComments = await commentService.getCommentsByTask(currentTask.id);
      // Sort by createdAt (oldest first)
      const sortedComments = fetchedComments.sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setComments(sortedComments);
    } catch (error: any) {
      console.error('Error loading comments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load comments',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingComments(false);
    }
  }, [currentTask?.id, toast]);

  useEffect(() => {
    if (open && currentTask?.id) {
      loadTimesheetEntries();
      loadComments();
    }
  }, [open, currentTask?.id, loadTimesheetEntries, loadComments]);

  // Load all mention users when dialog opens (for exact matching in comments)
  useEffect(() => {
    const loadAllMentionUsers = async () => {
      try {
        const users = await getEmployeesForMentions(''); // Empty string to get all users
        setAllMentionUsers(users);
      } catch (error) {
        console.error('Error loading all mention users:', error);
        setAllMentionUsers([]);
      }
    };
    if (open) {
      loadAllMentionUsers();
    }
  }, [open]);

  // Load employee list for assignees/followers when dialog opens
  useEffect(() => {
    const loadEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const emps = await getEmployeesForDropdown();
        // Normalize IDs and names
        const normalized = (Array.isArray(emps) ? emps : []).map((e: any) => ({
          id: e.id || e._id,
          _id: e._id || e.id,
          name: e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          email: e.email,
        })).filter((e: any) => e.name);
        setEmployeeOptions(normalized);
      } catch (err) {
        console.error('Failed to load employees for dropdown:', err);
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    if (open) {
      loadEmployees();
    }
  }, [open]);

  // Update assignees and followers when task changes
  useEffect(() => {
    if (task) {
      // Update description
      const taskDescription = task.description || '';
      setDescription(taskDescription);
      setOriginalDescription(taskDescription);
      
      // Update assignees
      const getPersonName = (p: any) => {
        if (!p) return 'Unknown User';
        const composed = `${p.firstName || ''} ${p.lastName || ''}`.trim();
        return composed || p.name || p.email || 'Unknown User';
      };

      if (task.assignees && task.assignees.length > 0) {
        const assigneeNames = task.assignees.map((a: any) => {
          // If backend returns only IDs, resolve via employeeOptions
          const id = typeof a === 'string' ? a : (a._id || a.id);
          if (id) {
            const match = employeeOptions.find((e) => e._id === id || e.id === id);
            if (match?.name) return match.name;
          }
          return getPersonName(a);
        });
        setSelectedAssignees(assigneeNames);
      } else if (task.assignedTo?.name) {
        setSelectedAssignees([task.assignedTo.name]);
      }
      
      // Update followers
      if (task.followers && task.followers.length > 0) {
        const followerNames = task.followers.map((f: any) => {
          const id = typeof f === 'string' ? f : (f._id || f.id);
          if (id) {
            const match = employeeOptions.find((e) => e._id === id || e.id === id);
            if (match?.name) return match.name;
          }
          return getPersonName(f);
        });
        setSelectedFollowers(followerNames);
      } else {
        setSelectedFollowers([]);
      }
    }
  }, [task, employeeOptions]);

  // Timer effect - update seconds for running timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timerState === 'running' && activeTimerEntry) {
      interval = setInterval(() => {
        const startTime = new Date(activeTimerEntry.startTime).getTime();
        const now = Date.now();
        const seconds = Math.floor((now - startTime) / 1000);
        setTimerSeconds(seconds);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerState, activeTimerEntry]);

  // Close mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMentionDropdown &&
        mentionDropdownRef.current &&
        commentTextareaRef.current &&
        !mentionDropdownRef.current.contains(event.target as Node) &&
        !commentTextareaRef.current.contains(event.target as Node)
      ) {
        setShowMentionDropdown(false);
      }
    };

    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMentionDropdown]);

  // Load timesheet entries for this task is defined above with useCallback

  // No active timer API; we only track the timer started in this dialog session

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatShortTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleTimerAction = async () => {
    if (!task?.id) return;
    
    setTimerLoading(true);
    try {
      if (timerState === 'stopped') {
        // Start timer
        const entry = await timesheetService.startTimer(task.id, timerNotes);
        setActiveTimerEntry(entry);
        setTimerState('running');
        setTimerSeconds(0);
        setTimerNotes('');
        toast({
          title: 'Timer Started',
          description: 'Your time tracking has begun',
        });
      } else if (timerState === 'running' && activeTimerEntry) {
        // Stop timer
        await timesheetService.stopTimer(activeTimerEntry._id);
        setTimerState('stopped');
        setTimerSeconds(0);
        setActiveTimerEntry(null);
        // Reload timesheet entries to show the completed entry
        await loadTimesheetEntries();
        toast({
          title: 'Timer Stopped',
          description: 'Time entry has been saved',
        });
      }
    } catch (error: any) {
      console.error('Error handling timer action:', error);
      const apiMessage = error?.response?.data?.message;
      const msg = typeof apiMessage === 'string' ? apiMessage : '';
      
      // Handle "An active timer already exists" error by fetching and resuming it
      if (msg.toLowerCase().includes('active timer already exists') || msg.toLowerCase().includes('already exists for this member')) {
        try {
          // Reload timesheet entries to find the active timer for the current user
          const entries = await timesheetService.getTimesheetEntriesByTask(task.id);
          const activeEntry = entries.find((entry: TimesheetEntry) => 
            !entry.endTime && 
            (entry.employeeId === user?.id || entry.employee?._id === user?.id)
          );
          
          if (activeEntry) {
            // Resume the active timer
            setActiveTimerEntry(activeEntry);
            setTimerState('running');
            const startTime = new Date(activeEntry.startTime).getTime();
            const now = Date.now();
            const elapsedSeconds = Math.floor((now - startTime) / 1000);
            setTimerSeconds(elapsedSeconds);
            
            toast({
              title: 'Timer Resumed',
              description: 'Your existing timer has been resumed',
            });
            // Reload timesheet entries to update the UI
            await loadTimesheetEntries();
          } else {
            toast({
              title: 'Error',
              description: 'An active timer exists but could not be found. Please refresh the page.',
              variant: 'destructive',
            });
          }
        } catch (fetchError) {
          console.error('Error fetching active timer:', fetchError);
          toast({
            title: 'Error',
            description: 'An active timer exists but could not be loaded. Please refresh the page.',
            variant: 'destructive',
          });
        }
      } else if (msg.toLowerCase().includes('only task assignees') || msg === 'Only task assignees can start timers') {
        toast({
          title: 'Permission required',
          description: 'Only task assignees can start timers. Please assign the task to yourself or contact the project owner.',
          variant: 'destructive',
        });
        sonnerToast.error('Only task assignees can start timers');
      } else {
        toast({
          title: 'Error',
          description: apiMessage || 'Failed to update timer',
          variant: 'destructive',
        });
        if (apiMessage) {
          sonnerToast.error(apiMessage);
        }
      }
    } finally {
      setTimerLoading(false);
    }
  };


  const calculateTimeSpent = (startTime: string, endTime?: string, durationSeconds?: number) => {
    // If duration is provided by API, use it directly
    if (durationSeconds && durationSeconds > 0) {
      const diffHours = Math.floor(durationSeconds / 3600);
      const diffMinutes = Math.floor((durationSeconds % 3600) / 60);
      const decimalHours = durationSeconds / 3600;
      
      return {
        hours: `${diffHours.toString().padStart(2, '0')}:${diffMinutes.toString().padStart(2, '0')}`,
        decimal: decimalHours.toFixed(2)
      };
    }
    
    // Otherwise calculate from start/end times
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    
    // Handle negative durations (shouldn't happen but just in case)
    if (diffMs < 0) {
      console.warn('Negative time duration detected:', { startTime, endTime, diffMs });
      return {
        hours: '00:00',
        decimal: '0.00'
      };
    }
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const decimalHours = diffHours + (diffMinutes / 60);
    
    console.log('Calculated time:', { startTime, endTime, diffMs, diffHours, diffMinutes, decimalHours });
    
    return {
      hours: `${diffHours.toString().padStart(2, '0')}:${diffMinutes.toString().padStart(2, '0')}`,
      decimal: decimalHours.toFixed(2)
    };
  };

  // Removed delete integration from actions as per requirement

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // For datetime-local input value
  const toLocalInputValue = (iso: string) => {
    try {
      const d = new Date(iso);
      const pad = (n: number) => n.toString().padStart(2, '0');
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch {
      return '';
    }
  };

  const onEditEntry = (entry: TimesheetEntry) => {
    setEditingEntryId(entry._id);
    setEditStartTime(toLocalInputValue(entry.startTime));
    setEditEndTime(entry.endTime ? toLocalInputValue(entry.endTime) : '');
    
    // Find the member name from the entry's employee data or task assignees
    const apiName = entry.employee ? `${entry.employee.firstName} ${entry.employee.lastName}`.trim() : '';
    const memberName = apiName || task?.assignedTo?.name || '';
    setEditMember(memberName);
    
    setEditNote(entry.notes || '');
  };

  const onCancelEdit = () => {
    setEditingEntryId(null);
    setEditStartTime('');
    setEditEndTime('');
    setEditMember('');
    setEditNote('');
  };

  const onSaveEdit = async () => {
    if (!editingEntryId) return;
    
    try {
      // Find the member ID from the selected member name
      const assigneeOptions = (task?.assignees && task.assignees.length > 0)
        ? task.assignees.map((a) => ({ id: a._id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email }))
        : employeeOptions;
      
      const selectedMember = assigneeOptions.find(emp => emp.name === editMember);
      const memberId = selectedMember?.id;
      
      if (!memberId) {
        toast({
          title: 'Error',
          description: 'Please select a valid member',
          variant: 'destructive',
        });
        return;
      }
      
      // Prepare update data
      const updates: any = {
        memberId: memberId,
        notes: editNote,
      };
      
      if (editStartTime) {
        updates.startTime = new Date(editStartTime).toISOString();
      }
      
      if (editEndTime) {
        updates.endTime = new Date(editEndTime).toISOString();
      }
      
      // Call the API
      const updatedEntry = await timesheetService.updateTimesheetEntry(editingEntryId, updates);
      
      // Update local state
      setTimesheets(prev => prev.map(e => 
        e._id === editingEntryId ? updatedEntry : e
      ));
      
      // Close edit mode
      onCancelEdit();
      
      toast({
        title: 'Success',
        description: 'Timesheet entry updated successfully',
      });
      
    } catch (error: any) {
      console.error('Error updating timesheet entry:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update timesheet entry',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSaveDescription = async () => {
    if (!currentTask?.id) return;
    
    setIsSavingDescription(true);
    try {
      // Update task description via API
      await taskService.updateTask(currentTask.id, {
        description: description
      });
      
      // Update local state
      setLoadedTask((prev: any) => {
        if (!prev) return prev;
        return { ...prev, description };
      });
      
      // Update original description to the new saved value
      setOriginalDescription(description);
      
      // Notify parent component
      onTaskUpdate?.(currentTask.id, { description });
      
      // Exit edit mode
      setIsEditingDescription(false);
      
      toast({
        title: 'Success',
        description: 'Description updated successfully',
      });
    } catch (error: any) {
      console.error('Error saving description:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update description',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleCancelDescription = () => {
    // Reset description to original value when edit mode was entered
    setDescription(originalDescription);
    setIsEditingDescription(false);
  };

  // Helper function to render comment with highlighted mentions
  const renderCommentWithMentions = (content: string) => {
    if (!content || typeof content !== 'string') {
      return <span>{content}</span>;
    }

    // Build list of exact mention patterns from user list
    // Only match exact mentions like "@Super Admin" or "@kevin patel"
    const mentionPatterns = allMentionUsers.map(user => {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      return `@${fullName}`;
    });

    // Sort by length (longest first) to match longer names first
    // This prevents matching "@Super" when the actual mention is "@Super Admin"
    mentionPatterns.sort((a, b) => b.length - a.length);

    const parts: Array<{ text: string; isMention: boolean; start: number; end: number }> = [];
    let lastIndex = 0;

    // Find all exact matches from the mention list
    const matches: Array<{ text: string; start: number; end: number }> = [];

    for (const pattern of mentionPatterns) {
      let searchIndex = 0;
      while (searchIndex < content.length) {
        const index = content.indexOf(pattern, searchIndex);
        if (index === -1) break;

        const endIndex = index + pattern.length;
        
        // Check if this is an exact match (not part of a larger word)
        // The mention must be at word boundary (start of string, after space/punctuation, or end of string)
        const charBefore = index > 0 ? content[index - 1] : '';
        const charAfter = endIndex < content.length ? content[endIndex] : '';
        
        // Valid mention boundary: start of string or preceded by space/punctuation/newline
        const isValidStart = index === 0 || /[\s.,!?;:\n\r]/.test(charBefore);
        // Valid mention boundary: end of string or followed by space/punctuation/newline
        const isValidEnd = endIndex >= content.length || /[\s.,!?;:\n\r]/.test(charAfter);

        if (isValidStart && isValidEnd) {
          matches.push({ text: pattern, start: index, end: endIndex });
        }

        searchIndex = index + 1;
      }
    }

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep longest/earliest)
    const validMatches: Array<{ text: string; start: number; end: number }> = [];
    for (const match of matches) {
      const overlaps = validMatches.some(existing => 
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end) ||
        (match.start <= existing.start && match.end >= existing.end)
      );
      if (!overlaps) {
        validMatches.push(match);
      }
    }

    // Build parts array from valid matches
    for (const match of validMatches) {
      // Add text before this mention (non-highlighted)
      if (match.start > lastIndex) {
        const beforeText = content.substring(lastIndex, match.start);
        if (beforeText.length > 0) {
          parts.push({ text: beforeText, isMention: false, start: lastIndex, end: match.start });
        }
      }

      // Add ONLY the exact mention match (highlighted)
      parts.push({ text: match.text, isMention: true, start: match.start, end: match.end });
      
      lastIndex = match.end;
    }

    // Add any remaining text after all mentions (non-highlighted)
    if (lastIndex < content.length) {
      const afterText = content.substring(lastIndex);
      if (afterText.length > 0) {
        parts.push({ text: afterText, isMention: false, start: lastIndex, end: content.length });
      }
    }

    // If no mentions found, return original content
    if (parts.length === 0) {
      return <span>{content}</span>;
    }

    // Render: ONLY exact mention matches get highlighted, everything else is normal
    return (
      <>
        {parts.map((part, index) => (
          part.isMention ? (
            <span key={`mention-${index}-${part.start}`} className="bg-blue-100 text-blue-800 font-medium px-1 rounded">
              {part.text}
            </span>
          ) : (
            <span key={`text-${index}-${part.start}`}>{part.text}</span>
          )
        ))}
      </>
    );
  };

  // Fetch mention users
  const fetchMentionUsers = useCallback(async (search: string = '') => {
    try {
      const users = await getEmployeesForMentions(search);
      setMentionUsers(users);
    } catch (error) {
      console.error('Error fetching mention users:', error);
      setMentionUsers([]);
    }
  }, []);

  // Handle comment input change and detect '@'
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);
    setMentionCursorPosition(cursorPos);

    // Check if '@' is being typed
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
      if (lastAtIndex !== -1) {
        // Check if there's a space after '@' (meaning it's not a mention)
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          // Show mention dropdown
          const searchQuery = textAfterAt;
          setShowMentionDropdown(true);
          fetchMentionUsers(searchQuery);
          return;
        }
      }
    
    // Hide dropdown if '@' is not found or space is present
    setShowMentionDropdown(false);
  };

  // Handle mention selection
  const handleSelectMention = (user: MentionUser) => {
    const textBeforeCursor = newComment.substring(0, mentionCursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textBeforeAt = newComment.substring(0, lastAtIndex);
      const textAfterCursor = newComment.substring(mentionCursorPosition);
      const userName = `${user.firstName} ${user.lastName}`;
      const mentionText = `@${userName} `;
      
      const updatedComment = textBeforeAt + mentionText + textAfterCursor;
      setNewComment(updatedComment);
      
      // Store tagged user info
      const mentionPosition = textBeforeAt.length;
      setTaggedUsers(prev => [...prev, {
        userId: user._id,
        name: userName,
        position: mentionPosition
      }]);
      
      // Hide dropdown and reset
      setShowMentionDropdown(false);
      
      // Set cursor position after the mention
      setTimeout(() => {
        if (commentTextareaRef.current) {
          const newCursorPos = textBeforeAt.length + mentionText.length;
          commentTextareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          commentTextareaRef.current.focus();
        }
      }, 0);
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        // Basic keyboard handling - can be enhanced with focus management
        if (e.key === 'Enter' && mentionUsers.length > 0) {
          handleSelectMention(mentionUsers[0]);
        } else if (e.key === 'Escape') {
          setShowMentionDropdown(false);
        }
        return;
      }
    }
    
    // Original Ctrl+Enter handler
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleAddComment();
    }
  };

  // Comment handlers
  const handleAddComment = async () => {
    if (!currentTask?.id || !newComment.trim()) return;
    
    setIsSubmittingComment(true);
    try {
      // Extract tagged user IDs from the taggedUsers array
      const taggedUserIds = taggedUsers.map(tag => tag.userId);
      
      const createdComment = await commentService.createComment(currentTask.id, {
        content: newComment.trim(),
        taggedUsers: taggedUserIds.length > 0 ? taggedUserIds : undefined
      });
      setComments(prev => [...prev, createdComment]);
      setNewComment('');
      setTaggedUsers([]);
      setShowMentionDropdown(false);
      toast({
        title: 'Success',
        description: 'Comment added successfully',
      });
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add comment',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleStartEditComment = (comment: Comment) => {
    setEditingCommentId(comment._id);
    setEditingCommentContent(comment.content);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleSaveEditComment = async () => {
    if (!currentTask?.id || !editingCommentId || !editingCommentContent.trim()) return;
    
    try {
      const updatedComment = await commentService.updateComment(
        currentTask.id,
        editingCommentId,
        { content: editingCommentContent.trim() }
      );
      setComments(prev =>
        prev.map(comment =>
          comment._id === editingCommentId ? updatedComment : comment
        )
      );
      setEditingCommentId(null);
      setEditingCommentContent('');
      toast({
        title: 'Success',
        description: 'Comment updated successfully',
      });
    } catch (error: any) {
      console.error('Error updating comment:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update comment',
        variant: 'destructive',
      });
    }
  };

  const handleRequestDeleteComment = (comment: Comment) => {
    setCommentToDelete(comment);
    setIsDeleteCommentDialogOpen(true);
  };

  const handleDeleteComment = async () => {
    if (!currentTask?.id || !commentToDelete) return;
    
    setIsDeletingComment(true);
    try {
      await commentService.deleteComment(currentTask.id, commentToDelete._id);
      setComments(prev => prev.filter(comment => comment._id !== commentToDelete._id));
      toast({
        title: 'Success',
        description: 'Comment deleted successfully',
      });
      setCommentToDelete(null);
      setIsDeleteCommentDialogOpen(false);
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete comment',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingComment(false);
    }
  };

  // Helper function to convert assignee/follower names to IDs
  const convertNamesToIds = (names: string[]): string[] => {
    return names
      .map((name) => {
        // First try to find in employeeOptions
        const employee = employeeOptions.find((e) => e.name === name);
        if (employee) {
          return employee._id || employee.id;
        }
        // Try to find in current task assignees/followers
        const matchedAssignee = matchPersonByNameOrEmail(currentTask?.assignees, name);
        if (matchedAssignee) {
          return matchedAssignee._id;
        }
        const matchedFollower = matchPersonByNameOrEmail(currentTask?.followers, name);
        if (matchedFollower) {
          return matchedFollower._id;
        }
        return null;
      })
      .filter((id): id is string => Boolean(id));
  };

  // Handler to add assignee
  const handleAddAssignee = async (userName: string) => {
    if (!currentTask?.id || isUpdatingAssignees) return;
    
    const previousAssignees = selectedAssignees;
    const newAssignees = [...selectedAssignees, userName];
    setSelectedAssignees(newAssignees);
    setIsUpdatingAssignees(true);
    
    try {
      const assigneeIds = convertNamesToIds(newAssignees);
      await taskService.updateTask(currentTask.id, {
        assignees: assigneeIds,
      });
      
      // Refetch task to get updated data
      const apiTask = await taskService.getTaskById(currentTask.id);
      const uiTask = mapApiTaskToUi(apiTask as any);
      setLoadedTask(uiTask as any);
      
      // Update selected assignees from the fetched data
      if (uiTask.assignees && uiTask.assignees.length > 0) {
        setSelectedAssignees(uiTask.assignees.map((a: any) => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email));
      }
      
      // Notify parent component
      onTaskUpdate?.(currentTask.id, { assignees: uiTask.assignees });
      
      toast({
        title: 'Success',
        description: 'Assignee added successfully',
      });
    } catch (error: any) {
      console.error('Error adding assignee:', error);
      // Revert local state on error
      setSelectedAssignees(previousAssignees);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add assignee',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingAssignees(false);
    }
  };

  // Handler to remove assignee
  const handleRemoveAssignee = async (userName: string) => {
    if (!currentTask?.id || isUpdatingAssignees) return;
    
    const previousAssignees = selectedAssignees;
    const newAssignees = selectedAssignees.filter((a) => a !== userName);
    setSelectedAssignees(newAssignees);
    setIsUpdatingAssignees(true);
    
    try {
      const assigneeIds = convertNamesToIds(newAssignees);
      await taskService.updateTask(currentTask.id, {
        assignees: assigneeIds,
      });
      
      // Refetch task to get updated data
      const apiTask = await taskService.getTaskById(currentTask.id);
      const uiTask = mapApiTaskToUi(apiTask as any);
      setLoadedTask(uiTask as any);
      
      // Update selected assignees from the fetched data
      if (uiTask.assignees && uiTask.assignees.length > 0) {
        setSelectedAssignees(uiTask.assignees.map((a: any) => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email));
      } else {
        setSelectedAssignees([]);
      }
      
      // Notify parent component
      onTaskUpdate?.(currentTask.id, { assignees: uiTask.assignees || [] });
      
      toast({
        title: 'Success',
        description: 'Assignee removed successfully',
      });
    } catch (error: any) {
      console.error('Error removing assignee:', error);
      // Revert local state on error
      setSelectedAssignees(previousAssignees);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to remove assignee',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingAssignees(false);
    }
  };

  // Handler to add follower
  const handleAddFollower = async (userName: string) => {
    if (!currentTask?.id || isUpdatingFollowers) return;
    
    const previousFollowers = selectedFollowers;
    const newFollowers = [...selectedFollowers, userName];
    setSelectedFollowers(newFollowers);
    setIsUpdatingFollowers(true);
    
    try {
      const followerIds = convertNamesToIds(newFollowers);
      await taskService.updateTask(currentTask.id, {
        followers: followerIds,
      });
      
      // Refetch task to get updated data
      const apiTask = await taskService.getTaskById(currentTask.id);
      const uiTask = mapApiTaskToUi(apiTask as any);
      setLoadedTask(uiTask as any);
      
      // Update selected followers from the fetched data
      if (uiTask.followers && uiTask.followers.length > 0) {
        setSelectedFollowers(uiTask.followers.map((f: any) => `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.email));
      }
      
      // Notify parent component
      onTaskUpdate?.(currentTask.id, { followers: uiTask.followers });
      
      toast({
        title: 'Success',
        description: 'Follower added successfully',
      });
    } catch (error: any) {
      console.error('Error adding follower:', error);
      // Revert local state on error
      setSelectedFollowers(previousFollowers);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add follower',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingFollowers(false);
    }
  };

  // Handler to remove follower
  const handleRemoveFollower = async (userName: string) => {
    if (!currentTask?.id || isUpdatingFollowers) return;
    
    const previousFollowers = selectedFollowers;
    const newFollowers = selectedFollowers.filter((f) => f !== userName);
    setSelectedFollowers(newFollowers);
    setIsUpdatingFollowers(true);
    
    try {
      const followerIds = convertNamesToIds(newFollowers);
      await taskService.updateTask(currentTask.id, {
        followers: followerIds,
      });
      
      // Refetch task to get updated data
      const apiTask = await taskService.getTaskById(currentTask.id);
      const uiTask = mapApiTaskToUi(apiTask as any);
      setLoadedTask(uiTask as any);
      
      // Update selected followers from the fetched data
      if (uiTask.followers && uiTask.followers.length > 0) {
        setSelectedFollowers(uiTask.followers.map((f: any) => `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.email));
      } else {
        setSelectedFollowers([]);
      }
      
      // Notify parent component
      onTaskUpdate?.(currentTask.id, { followers: uiTask.followers || [] });
      
      toast({
        title: 'Success',
        description: 'Follower removed successfully',
      });
    } catch (error: any) {
      console.error('Error removing follower:', error);
      // Revert local state on error
      setSelectedFollowers(previousFollowers);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to remove follower',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingFollowers(false);
    }
  };

  const filteredAssignees = employeeOptions.filter(user =>
    user.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const filteredFollowers = employeeOptions.filter(user =>
    user.name.toLowerCase().includes(followerSearch.toLowerCase())
  );

  const totalLoggedTime = (() => {
    const timesheetHours = timesheets
      .filter(ts => ts.endTime) // Only count completed entries
      .reduce((total, ts) => {
        const timeSpent = calculateTimeSpent(ts.startTime, ts.endTime, ts.duration);
      return total + parseFloat(timeSpent.decimal);
    }, 0);
    const timerHours = (timerState === 'running' ? timerSeconds : 0) / 3600;
    return (timesheetHours + timerHours) * 3600; // Convert back to seconds for formatShortTime
  })();

  // Early return after all hooks have been declared
  if (!currentTask) return null;

  // Determine if current user is a member/assignee of this task
  const currentUserId = user?.id;
  const assigneeIds = Array.isArray((currentTask as any).assignees)
    ? (currentTask as any).assignees.map((a: any) => (typeof a === 'string' ? a : (a._id || a.id))).filter(Boolean)
    : [];
  const isTaskMember = !!currentUserId && assigneeIds.includes(currentUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-hidden flex">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgb(209,209,209)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:[display:none] [scrollbar-width:thin] [scrollbar-color:rgb(209,209,209)_transparent]">
          <div className="px-6 py-4">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {currentTask.name}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Created at {formatDate(currentTask.startDate)}</span>
                  
                </div>
              </div>
              <Badge className={cn('px-3 py-1 flex items-center space-x-1', getStatusColor(currentTask.status))}>
                {getStatusIcon(currentTask.status)}
                <span>
                  {typeof currentTask.status === 'object' && currentTask.status !== null
                    ? (currentTask.status.name || currentTask.status.status || String(currentTask.status))
                    : String(currentTask.status || 'Not Started')}
                </span>
              </Badge>
            </div>
            
            {/* Timer and Actions */}
            <div className="flex items-center space-x-4">
              {isTaskMember && timerState === 'stopped' && (
                <div className="flex items-center space-x-2">
                  <Input
                    placeholder="Add a note (optional)"
                    value={timerNotes}
                    onChange={(e) => setTimerNotes(e.target.value)}
                    className="w-64"
                  />
                </div>
              )}
              {isTaskMember && (
                <Button
                  onClick={handleTimerAction}
                  disabled={timerLoading}
                  className={cn(
                    "flex items-center space-x-2",
                    timerState === 'running' ? "bg-orange-500 hover:bg-orange-600" : "bg-green-500 hover:bg-green-600"
                  )}
                >
                  {timerLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : timerState === 'running' ? (
                    <PauseCircle className="h-4 w-4" />
                  ) : (
                    <PlayCircle className="h-4 w-4" />
                  )}
                  <span>{timerState === 'running' ? 'Stop Timer' : 'Start Timer'}</span>
                  {timerState === 'running' && <span className="font-mono">{formatTime(timerSeconds)}</span>}
                </Button>
              )}
              
              
              {/* Timesheet Icon */}
              <Button
                onClick={() => setShowTimesheet(!showTimesheet)}
                variant={showTimesheet ? "default" : "outline"}
                className={cn(
                  "flex items-center space-x-2",
                  showTimesheet ? "bg-blue-500 hover:bg-blue-600 text-white" : ""
                )}
              >
                <List className="h-4 w-4" />
                <span>Timesheet</span>
              </Button>
              
              {/* Edit Task Button - visible only to task members with edit permission */}
              {isTaskMember && hasPermission('project_management.task.edit') && (
                <Button
                  onClick={() => setShowEditDialog(true)}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Edit Task</span>
                </Button>
              )}
              
              <div className="flex items-center space-x-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-gray-400" />
                <span className="text-gray-400">Checklist</span>
              </div>
            </div>
          </div>

          {/* Timesheet Drawer */}
          {showTimesheet && (
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timesheet</h3>
              
              {/* Timesheet Table */}
              <div className="mb-4">
                {isLoadingTimesheet ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : timesheets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>No timesheet entries yet</p>
                    <p className="text-sm">Start the timer to track your time</p>
                  </div>
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Member</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Start Time</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">End Time</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Time Spent</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Notes</th>
                        {isTaskMember && (
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                        {timesheets.map((timesheet, index) => {
                          const timeSpent = calculateTimeSpent(timesheet.startTime, timesheet.endTime, timesheet.duration);
                          // Display the member name from the timesheet entry's member object
                          const memberName = timesheet.employee 
                            ? `${timesheet.employee.firstName || ''} ${timesheet.employee.lastName || ''}`.trim() || 'Unknown'
                            : currentTask.assignedTo?.name || 'Unknown';
                          
                          // Debug logging for first entry
                          if (index === 0) {
                            console.log('[TaskDetailDialog] Rendering timesheet entry:', {
                              startTime: timesheet.startTime,
                              endTime: timesheet.endTime,
                              formattedStart: formatDateTime(timesheet.startTime),
                              formattedEnd: timesheet.endTime ? formatDateTime(timesheet.endTime) : 'N/A',
                              duration: timesheet.duration,
                              timeSpent,
                              memberObject: timesheet.employee,
                              memberName
                            });
                          }
                          
                        return (
                            <tr key={timesheet._id} className="border-b border-gray-100">
                              <td className="py-2 px-3 text-sm text-gray-900">{memberName}</td>
                            <td className="py-2 px-3 text-sm text-gray-600">{formatDateTime(timesheet.startTime)}</td>
                            <td className="py-2 px-3 text-sm text-gray-600">
                                {timesheet.endTime ? formatDateTime(timesheet.endTime) : (
                                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                                    Running
                                  </Badge>
                                )}
                              </td>
                              <td className="py-2 px-3 text-sm text-gray-900 font-medium">
                                <div className="flex flex-col">
                                  <span>{timeSpent.hours}</span>
                                  <span className="text-xs text-gray-500">({timeSpent.decimal} hrs)</span>
                              </div>
                            </td>
                              <td className="py-2 px-3 text-sm text-gray-600">
                                {timesheet.notes || '-'}
                              </td>
                              {isTaskMember && (
                                <td className="py-2 px-3">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => onEditEntry(timesheet)}
                                  >
                                    <Edit3 className="h-4 w-4" />
                                  </Button>
                                </td>
                              )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>

              {/* Inline Edit Drawer */}
              {isTaskMember && editingEntryId && (
                <div className="border-t border-gray-200 pt-4 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <Input
                        type="datetime-local"
                        value={editStartTime}
                        onChange={(e) => setEditStartTime(e.target.value)}
                      />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <Input
                        type="datetime-local"
                        value={editEndTime}
                        onChange={(e) => setEditEndTime(e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
                      {(() => {
                        const assigneeOptions = ((currentTask as any)?.assignees && (currentTask as any).assignees.length > 0)
                          ? (currentTask as any).assignees.map((a: any) => ({ id: a._id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email }))
                          : employeeOptions;
                        return (
                      <Select value={editMember} onValueChange={setEditMember}>
                      <SelectTrigger>
                          <SelectValue placeholder="Select member" />
                      </SelectTrigger>
                      <SelectContent>
                          {assigneeOptions.map((emp: { id: string; name: string }) => (
                            <SelectItem key={emp.id} value={emp.name}>{emp.name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                        );
                      })()}
                  </div>
                    <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <Textarea
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Add a note about this timesheet entry..."
                    className="min-h-[80px]"
                  />
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={onCancelEdit}>Cancel</Button>
                    <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={onSaveEdit}>Save</Button>
              </div>
                </div>
              )}

            </div>
          )}

          {/* Description */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Description</h2>
              {!isEditingDescription && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Store current description as original when entering edit mode
                    setOriginalDescription(description);
                    setIsEditingDescription(true);
                  }}
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
              )}
            </div>
            {isEditingDescription ? (
              <div className="space-y-2">
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px]"
                  placeholder="Enter task description..."
                  disabled={isSavingDescription}
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelDescription}
                    disabled={isSavingDescription}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveDescription}
                    disabled={isSavingDescription}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isSavingDescription ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 bg-gray-50 p-4 rounded-md whitespace-pre-wrap">
                {description || 'No description provided'}
              </p>
            )}
          </div>

          {/* Comments Section */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
            
            {/* Comments List */}
            {isLoadingComments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : comments.length === 0 ? (
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-gray-500 text-sm">No comments yet. Add a comment to start the conversation.</p>
              </div>
            ) : (
              <div className="space-y-4 mb-4">
                {comments.map((comment) => {
                  const isEditing = editingCommentId === comment._id;
                  const isCommentOwner = user?.id === comment.createdBy._id;
                  const commentUser = comment.createdBy;
                  const displayName = `${commentUser.firstName || ''} ${commentUser.lastName || ''}`.trim() || commentUser.email || 'Unknown User';
                  const avatarUrl = buildImageUrlIfNeeded(commentUser.profilePicture);
                  const initials = getInitialsFromName(displayName);
                  const commentDate = new Date(comment.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });

                  return (
                    <div key={comment._id} className="bg-gray-50 p-4 rounded-md">
                      <div className="flex items-start space-x-3">
                        {/* Avatar */}
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 overflow-hidden flex-shrink-0">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={displayName}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <span className={`${avatarUrl ? 'hidden' : ''}`}>
                            {initials}
                          </span>
                        </div>

                        {/* Comment Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">{displayName}</span>
                              <span className="text-xs text-gray-500">{commentDate}</span>
                              {comment.updatedAt !== comment.createdAt && (
                                <span className="text-xs text-gray-400">(edited)</span>
                              )}
                            </div>
                            {isCommentOwner && !isEditing && (
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-600 hover:text-blue-600"
                                  onClick={() => handleStartEditComment(comment)}
                                >
                                  <Edit3 className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-gray-600 hover:text-red-600"
                                  onClick={() => handleRequestDeleteComment(comment)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {isEditing ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editingCommentContent}
                                onChange={(e) => setEditingCommentContent(e.target.value)}
                                className="min-h-[80px]"
                                placeholder="Edit your comment..."
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelEditComment}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleSaveEditComment}
                                  disabled={!editingCommentContent.trim()}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {renderCommentWithMentions(comment.content)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Comment Form */}
            <div className="space-y-2 relative">
              <Textarea
                ref={commentTextareaRef}
                value={newComment}
                onChange={handleCommentChange}
                className="min-h-[100px]"
                placeholder="Add a comment... (Type @ to mention someone)"
                disabled={isSubmittingComment}
                onKeyDown={handleCommentKeyDown}
              />
              
              {/* Mention Dropdown */}
              {showMentionDropdown && mentionUsers.length > 0 && (
                <div
                  ref={mentionDropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgb(209,209,209)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:[display:none] [scrollbar-width:thin] [scrollbar-color:rgb(209,209,209)_transparent]"
                  style={{ top: '100%', marginTop: '4px' }}
                >
                  {mentionUsers.map((user) => {
                    const userName = `${user.firstName} ${user.lastName}`;
                    const avatarUrl = buildImageUrlIfNeeded(user.profilePicture);
                    const initials = getInitialsFromName(userName);
                    return (
                      <div
                        key={user._id}
                        onClick={() => handleSelectMention(user)}
                        className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 overflow-hidden flex-shrink-0">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={userName}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <span className={`${avatarUrl ? 'hidden' : ''}`}>
                            {initials}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">{userName}</div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                          {user.employeeId && (
                            <div className="text-xs text-gray-400">ID: {user.employeeId}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="flex items-center justify-end gap-2">
                <span className="text-xs text-gray-500">Press Ctrl+Enter to submit</span>
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isSubmittingComment}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isSubmittingComment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Comment'
                  )}
                </Button>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 border-l overflow-y-auto pt-12 mr-10 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgb(209,209,209)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:[display:none] [scrollbar-width:thin] [scrollbar-color:rgb(209,209,209)_transparent]">
          <div className="px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Info</h2>
          
          {/* Timer Info */}
          <div className="mb-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Your logged time:</span>
                <span className="text-green-600 font-medium">{formatShortTime(totalLoggedTime)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total logged time:</span>
                <span className="text-green-600 font-medium">{formatShortTime(totalLoggedTime)}</span>
              </div>
            </div>
          </div>

          {/* Assignees */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Assignees</h3>
            <div className="relative">
              <div
                className="border border-gray-200 rounded-md p-2 cursor-pointer hover:border-gray-300"
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
              >
                <span className="text-sm text-gray-500">Assign task to</span>
                <ChevronDown className="h-4 w-4 float-right text-gray-400" />
              </div>
              
              {showAssigneeDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search assignees..."
                      value={assigneeSearch}
                      onChange={(e) => setAssigneeSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="py-1 max-h-40 overflow-auto">
                    {filteredAssignees.map((user) => {
                      const isSelected = selectedAssignees.includes(user.name);
                      const isDisabled = isUpdatingAssignees || isSelected;
                      return (
                        <div
                          key={user.id}
                          className={cn(
                            "flex items-center space-x-2 px-3 py-2 cursor-pointer",
                            isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
                          )}
                          onClick={() => {
                            if (!isDisabled && !isSelected) {
                              handleAddAssignee(user.name);
                            }
                          }}
                        >
                          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">
                            {getInitialsFromName(user.name)}
                          </div>
                          <span className="text-sm">{user.name}</span>
                          {isUpdatingAssignees && (
                            <Loader2 className="h-3 w-3 ml-auto animate-spin text-gray-400" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Selected Assignees */}
              <div className="mt-2 space-y-2">
                {selectedAssignees.map((assignee, index) => {
                  const matchedAssignee = matchPersonByNameOrEmail(currentTask?.assignees, assignee);
                  const user = employeeOptions.find(u => u.name === assignee);
                  const displayName = matchedAssignee ? (buildFullName(matchedAssignee) || matchedAssignee.email || assignee) : (user?.name || assignee);
                  const avatarUrl = buildImageUrlIfNeeded(matchedAssignee?.profilePicture || matchedAssignee?.avatar);
                  const initials = getInitialsFromName(displayName);
                  return (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 overflow-hidden">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <span className={`${avatarUrl ? 'hidden' : ''}`}>
                          {initials}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">{displayName}</span>
                      {isUpdatingAssignees ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : (
                        <X
                          className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                          onClick={() => handleRemoveAssignee(assignee)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Followers */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Followers</h3>
            <div className="relative">
              <div
                className="border border-gray-200 rounded-md p-2 cursor-pointer hover:border-gray-300"
                onClick={() => setShowFollowerDropdown(!showFollowerDropdown)}
              >
                <span className="text-sm text-gray-500">Add Followers</span>
                <ChevronDown className="h-4 w-4 float-right text-gray-400" />
              </div>
              
              {showFollowerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search followers..."
                      value={followerSearch}
                      onChange={(e) => setFollowerSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <div className="py-1 max-h-40 overflow-auto">
                    {filteredFollowers.map((user) => {
                      const isSelected = selectedFollowers.includes(user.name);
                      const isDisabled = isUpdatingFollowers || isSelected;
                      return (
                        <div
                          key={user.id}
                          className={cn(
                            "flex items-center space-x-2 px-3 py-2 cursor-pointer",
                            isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
                          )}
                          onClick={() => {
                            if (!isDisabled && !isSelected) {
                              handleAddFollower(user.name);
                            }
                          }}
                        >
                          <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-medium text-green-800">
                            {getInitialsFromName(user.name)}
                          </div>
                          <span className="text-sm">{user.name}</span>
                          {isUpdatingFollowers && (
                            <Loader2 className="h-3 w-3 ml-auto animate-spin text-gray-400" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Selected Followers */}
              <div className="mt-2 space-y-2">
                {selectedFollowers.map((follower, index) => {
                  const matchedFollower = matchPersonByNameOrEmail(currentTask?.followers, follower);
                  const user = employeeOptions.find(u => u.name === follower);
                  const displayName = matchedFollower ? (buildFullName(matchedFollower) || matchedFollower.email || follower) : (user?.name || follower);
                  const avatarUrl = buildImageUrlIfNeeded(matchedFollower?.profilePicture);
                  const initials = getInitialsFromName(displayName);
                  return (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-xs font-medium text-green-800 overflow-hidden">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <span className={`${avatarUrl ? 'hidden' : ''}`}>
                          {initials}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">{displayName}</span>
                      {isUpdatingFollowers ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : (
                        <X
                          className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                          onClick={() => handleRemoveFollower(follower)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Start Date</h3>
              {/* <span className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">Edit</span> */}
              <p className="text-sm text-gray-700 mt-1">{formatDate(currentTask.startDate)}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Priority</h3>
              {/* <span className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">Edit</span> */}
              <div className="mt-1">
                <Badge className={cn('text-xs', getPriorityColor(currentTask.priority))}>
                  {currentTask.priority}
                </Badge>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mt-8">
            <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-2">Drop files here to upload</p>
              <Button variant="outline" size="sm">
                Choose from Dropbox
              </Button>
            </div>
          </div>
          </div>
        </div>
      </DialogContent>
      
      {/* Edit Task Dialog */}
      <EditTaskDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        task={currentTask}
        onTaskUpdate={(taskId, updates) => {
          // Handle task update callback
          console.log('Task updated in TaskDetailDialog:', taskId, updates);
          
          // Immediately reflect updates locally without page refresh
          if (updates) {
            // Update loadedTask state with the new updates
            setLoadedTask((prev: any) => {
              if (!prev || prev.id !== taskId) return prev;
              
              return {
                ...prev,
                name: updates.name !== undefined ? updates.name : prev.name,
                description: updates.description !== undefined ? updates.description : prev.description,
                status: updates.status !== undefined ? updates.status : prev.status,
                priority: updates.priority !== undefined ? updates.priority : prev.priority,
                startDate: updates.startDate !== undefined ? updates.startDate : prev.startDate,
                dueDate: updates.dueDate !== undefined ? updates.dueDate : prev.dueDate,
                estimatedHours: updates.estimatedHours !== undefined ? updates.estimatedHours : prev.estimatedHours,
                url: updates.url !== undefined ? updates.url : prev.url,
                tags: updates.tags !== undefined ? updates.tags : prev.tags,
                assignees: updates.assignees !== undefined ? updates.assignees : prev.assignees,
                followers: updates.followers !== undefined ? updates.followers : prev.followers,
              };
            });
            
      // Update description state
      if (updates.description !== undefined) {
        setDescription(updates.description);
        setOriginalDescription(updates.description);
      }
            
            // Update assignees and followers
            const toName = (item: any) => {
              if (!item) return 'Unknown User';
              const id = typeof item === 'string' ? item : (item._id || item.id);
              if (id) {
                const match = employeeOptions.find((e) => e._id === id || e.id === id);
                if (match?.name) return match.name;
              }
              const composed = `${item.firstName || ''} ${item.lastName || ''}`.trim();
              return composed || item.name || item.email || 'Unknown User';
            };

            if (Array.isArray(updates.assignees)) {
              setSelectedAssignees(updates.assignees.map(toName));
            }
            if (Array.isArray(updates.followers)) {
              setSelectedFollowers(updates.followers.map(toName));
            }
          }

          // Notify parent component to update task data
          onTaskUpdate?.(taskId, updates);
          
          // Optionally refetch task data to ensure we have the latest from server
          if (currentTask?.id === taskId) {
            const refetchTask = async () => {
              try {
                const apiTask = await taskService.getTaskById(taskId);
                const uiTask = mapApiTaskToUi(apiTask as any);
                setLoadedTask(uiTask as any);
                // Sync editable local states
                const taskDescription = uiTask.description || '';
                setDescription(taskDescription);
                setOriginalDescription(taskDescription);
                if (uiTask.assignees && uiTask.assignees.length > 0) {
                  setSelectedAssignees(uiTask.assignees.map((a: any) => `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email));
                } else if (uiTask.assignedTo?.name) {
                  setSelectedAssignees([uiTask.assignedTo.name]);
                }
                if (uiTask.followers && uiTask.followers.length > 0) {
                  setSelectedFollowers(uiTask.followers.map((f: any) => `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.email));
                }
              } catch (error) {
                console.error('Failed to refetch task details:', error);
                // Don't show error toast - we already updated locally
              }
            };
            refetchTask();
          }
        }}
      />

      {/* Delete Comment Confirmation */}
      <AlertDialog open={isDeleteCommentDialogOpen} onOpenChange={(open) => {
        setIsDeleteCommentDialogOpen(open);
        if (!open) {
          setCommentToDelete(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700">
            {commentToDelete?.content}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingComment}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteComment}
              disabled={isDeletingComment}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingComment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Comment'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export default TaskDetailDialog;
